import { datadogRum } from '@datadog/browser-rum';

let vendorLoad: Promise<void> | undefined;

const loadVendor = async (): Promise<void> => {
  // Imported for their side effects, and deliberately NOT at module scope:
  // `config` assigns `window.IGLOO`, and `loader_only.js` injects the FraudForce
  // <script> tags. At module scope, every consumer of this module — including
  // anything that merely reached it through a barrel re-export — would load and
  // run FraudForce whether or not it ever asked for a fingerprint.
  await import('../../vendor/iovation/config');
  await import('../../vendor/iovation/loader_only.js');
};

/**
 * Starts loading the iovation FraudForce vendor scripts.
 *
 * Safe to call repeatedly; the vendor scripts are loaded at most once.
 *
 * Call this as soon as a fingerprint is known to be needed. FraudForce needs time
 * to compute a blackbox after its scripts run, so a `getFingerprint()` call that
 * immediately follows the first load will usually report
 * `IOVATION_FINGERPRINT_NOT_FINISHED`.
 */
export async function initFraudForce(): Promise<void> {
  if (!vendorLoad) {
    vendorLoad = loadVendor().catch((e: unknown) => {
      // Clear the cache so a later call can retry a transient load failure.
      vendorLoad = undefined;

      datadogRum.addError(e as Error, {
        context: 'IOVATION_VENDOR_LOAD_FAILED',
      });
    });
  }

  await vendorLoad;
}

const onDemandFingerprintFetcher = (): string | undefined => {
  try {
    if (!window.IGLOO.getBlackbox) {
      datadogRum.addAction('IOVATION_FINGERPRINT_NOT_AVAILABLE');

      return undefined;
    }

    const result = window.IGLOO.getBlackbox();

    if (result.finished) {
      datadogRum.addAction('IOVATION_FINGERPRINT_OBTAINED');

      return result.blackbox;
    }

    datadogRum.addAction('IOVATION_FINGERPRINT_NOT_FINISHED');
  } catch (e) {
    datadogRum.addError(e as Error, { context: 'IOVATION_FINGERPRINT_FAILED' });
  }

  return undefined;
};

export async function getFingerprint(): Promise<string | undefined> {
  await initFraudForce();

  return onDemandFingerprintFetcher();
}
